import React, { useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useSortable, SortableContext, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import _ from "lodash";
import { ref, uploadBytesResumable } from "firebase/storage";
import { useDropzone } from "react-dropzone";
import { nanoid } from "nanoid";
import { Trash2, Plus, GripVertical } from "lucide-react";
import GracefulImage from "react-graceful-image";

import { storage } from "../../../firebase.js";
import Message from "../../../components/Message.js";
import { isResolutionOk, tooSmallMessage } from "../../../imageValidation.js";

const imageCache = {};

function SortableItem(props) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const src = `https://storage.googleapis.com/feestjegeven-nl.appspot.com/venues/${props.venueId}/images/${props.id}-thumb.jpg`;

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="relative group">
      <GracefulImage
        src={imageCache[props.id] || src}
        className={`w-full aspect-square object-cover rounded-sm ${props.grayedOut ? "grayscale opacity-60" : ""}`}
        alt=""
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-fast rounded-sm flex items-center justify-center">
        <span
          {...listeners}
          className="absolute top-1.5 left-1.5 p-1 bg-white/80 rounded-sm cursor-grab opacity-0 group-hover:opacity-100 transition-opacity duration-fast"
        >
          <GripVertical size={14} className="text-text-secondary" />
        </span>
        <button
          onClick={() => props.onImageRemove(props.id)}
          className="p-1.5 bg-error text-white rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-fast hover:bg-red-700"
          type="button"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function Gallery(props) {
  const [progress, setProgress] = useState(false);
  const [error, setError] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor));
  const filesRef = ref(storage, `venues/${props.objectID}/images`);

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    accept: "image/*",
    onDrop: (acceptedFiles) => {
      setError("");

      const file = acceptedFiles[0];
      const nid = nanoid(10).toLowerCase();
      const ext = file.name.split(".").pop();
      const image = new Image();

      image.onload = function () {
        if (!isResolutionOk(image.width, image.height)) {
          setError(tooSmallMessage(image.width, image.height));
          return false;
        }

        imageCache[nid] = image.src;

        const task = uploadBytesResumable(ref(filesRef, `${nid}.${ext}`), file);
        task.on(
          "state_changed",
          function (snapshot) {
            setProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
          },
          function (error) {
            console.log(error);
          },
          function () {
            setProgress(0);
            props.onImageAdd(nid);
          },
        );
      };

      image.src = URL.createObjectURL(file);
    },
  });

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={props.onSortEnd}>
          <SortableContext items={props.images} strategy={horizontalListSortingStrategy}>
            {_.map(props.images, (id, count) => (
              <SortableItem
                key={id}
                id={id}
                label={count + 1}
                onImageRemove={props.onImageRemove}
                venueId={props.objectID}
                grayedOut={!props.hasPremiumAccount && count > 1}
              />
            ))}
          </SortableContext>
        </DndContext>

        <div
          {...getRootProps()}
          className="flex flex-col items-center justify-center aspect-square border-2 border-dashed border-border hover:border-accent hover:bg-accent-subtle rounded-sm cursor-pointer transition-all duration-fast"
        >
          <input {...getInputProps()} />
          {progress > 1 ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-text-secondary font-medium" data-tabular="true">
                {progress}%
              </span>
            </div>
          ) : (
            <>
              <Plus size={24} strokeWidth={1.5} className="text-text-tertiary mb-1" />
              <span className="text-xs text-text-tertiary">Foto toevoegen</span>
            </>
          )}
        </div>
      </div>
      {error && (
        <div className="mt-3">
          <Message text={error} />
        </div>
      )}
    </div>
  );
}

export default Gallery;
